import type { Language } from '@eformulieren/engine'
import type { Text } from '@eformulieren/model'
import { BrandBlock } from '@eformulieren/renderer'
import type { ReactNode } from 'react'

/**
 * De productbalk boven de pagina: het merkblok en, zodra bekend, het kruimelpad naar deze
 * instantie (R-157).
 *
 * **Hier en niet in `Invulformulier`.** Dat component monteert ook de configurator zijn
 * `Voorbeeld`-paneel (`apps/configurator/src/app/voorbeeld/Voorbeeld.tsx`), dat al een eigen
 * merkblok toont in `Balk.tsx`; een tweede tegel binnen `Invulformulier` zou daar dubbel op het
 * scherm staan. De productbalk hoort bij de pagina die de pod serveert, niet bij het formulier
 * zelf, en staat daarom hier, in `apps/runtime/pagina/`.
 *
 * **Geen "ronde"-stap in het kruimelpad.** Het canvas toont ronde &rarr; dossier &rarr;
 * instantietitel; `ronde` bestaat als veld nergens op de instantie en toevoegen is een migratie
 * op de store voor een stap die zonder processysteem toch leeg blijft (`docs/dekking.md`,
 * "R-125 sluit hiermee gedeeltelijk"). Dit kruimelpad begint daarom bij het blok.
 */
export interface ProductBarProps {
  readonly taal: Language
  /** De weergavenaam van het bouwblok (ontwerp 9.5, R-125), als eerste stap in het kruimelpad. */
  readonly bloknaam?: Text
  /** De titel van de definitie zelf (`Projectie.titel`), voor deze taal. */
  readonly instantieTitle?: string
  /**
   * Handelingen rechts in de balk: de taalwissel op de inlog- en uitnodigingspagina.
   *
   * Op de formulierpagina staat de productbalk alleen zolang het formulier nog niet in beeld is;
   * daarna zijn merk en kruimelpad een slot in de kopbalk van het formulier
   * (`FillForm.barStart`), met delen erachter, zodat er één kopregel is (ontwerp van het
   * invulscherm, 29 oktober 2026).
   */
  readonly actions?: ReactNode
}

/**
 * Het merkblok met het kruimelpad, los te gebruiken als slot in de kopbalk van het formulier.
 *
 * **Geen ruwe dossierverwijzing meer in het kruimelpad** (ontwerp van het invulscherm). Het
 * kruimelpad noemde `dossier-sim-ce9d58`; dat zegt een invuller niets. Nu staan er het blok en de
 * titel van het formulier. Het dossier blijft zichtbaar in de contextkop (R-125), klein onder de
 * titel.
 */
export function BrandAndPath({
  taal: language,
  bloknaam: blockName,
  instantieTitle,
}: Pick<ProductBarProps, 'taal' | 'bloknaam' | 'instantieTitle'>): ReactNode {
  return (
    <>
      <BrandBlock />
      {blockName === undefined && instantieTitle === undefined ? null : (
        <>
          <span className="pod-productbalk__scheiding" aria-hidden="true" />
          <nav aria-label={BREADCRUMB[language]} className="pod-productbalk__kruimelpad">
            {blockName === undefined ? null : <span>{blockName[language]}</span>}
            {blockName === undefined || instantieTitle === undefined ? null : <span aria-hidden="true">&rsaquo;</span>}
            {instantieTitle === undefined ? null : (
              <span className="pod-productbalk__huidige" aria-current="page">
                {instantieTitle}
              </span>
            )}
          </nav>
        </>
      )}
    </>
  )
}

export function ProductBar({ taal: language, bloknaam: blockName, instantieTitle, actions }: ProductBarProps): ReactNode {
  return (
    <div className="pod-productbalk">
      <BrandAndPath
        taal={language}
        {...(blockName === undefined ? {} : { bloknaam: blockName })}
        {...(instantieTitle === undefined ? {} : { instantieTitle })}
      />
      {actions === undefined ? null : <div className="pod-productbalk__acties">{actions}</div>}
    </div>
  )
}

const BREADCRUMB: Text = { nl: 'Kruimelpad', en: 'Breadcrumb' }
